// useSidebarTree — recebe a definição nested do menu lateral e devolve
// uma representação flat (estilo Angular CDK Tree) que casa com virtual
// scroll e simplifica o render: 1 array → 1 loop, padding-left vem do
// level. Persistência do estado expandido por painel via localStorage.
//
// Uso:
//   const { visibleNodes, isExpanded, toggle, expandPathTo } =
//     useSidebarTree(menuItemsRef, { storageKey: 'sidebar-expanded:Conversation' });
//
// O composable NÃO conhece routing — quem chama é responsável por
// determinar o active node e invocar expandPathTo(activeId).

import { computed, ref, watch } from 'vue';

// Hidrata do localStorage (best effort — storage corrompido vira Set vazio).
const readFromStorage = key => {
  if (!key) return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const writeToStorage = (key, set) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // QuotaExceeded ou storage indisponivel — silencioso, persistência
    // é nice-to-have, não bloqueia a UI.
  }
};

export function useSidebarTree(menuItemsRef, options = {}) {
  const { storageKey = null, defaultExpanded = false } = options;

  // ref local por instância pra não vazar estado entre composables.
  // useStorage do @vueuse não lida bem com Set como default; persistência
  // manual via watch é mais previsível.
  const expandedIds = ref(readFromStorage(storageKey));

  if (storageKey) {
    watch(expandedIds, set => writeToStorage(storageKey, set), { deep: false });
  }

  // Flatten DFS preservando ordem original. Cada nó ganha `id`, `level`,
  // `parentId`, `hasChildren` além dos campos originais.
  const flatNodes = computed(() => {
    const out = [];
    const walk = (items, level, parentId) => {
      (items || []).forEach(item => {
        if (!item || !item.name) return;
        const hasChildren =
          Array.isArray(item.children) && item.children.length > 0;
        out.push({
          ...item,
          id: item.name,
          level,
          parentId,
          hasChildren,
        });
        if (hasChildren) walk(item.children, level + 1, item.name);
      });
    };
    walk(menuItemsRef.value, 0, null);
    return out;
  });

  // Helper: lista de IDs ancestrais (do pai mais próximo até a raiz).
  const getAncestorIds = (nodeId, byId) => {
    const result = [];
    let current = byId.get(nodeId)?.parentId ?? null;
    while (current) {
      result.push(current);
      current = byId.get(current)?.parentId ?? null;
    }
    return result;
  };

  const ancestorOpen = (parentId, expanded) =>
    defaultExpanded
      ? !expanded.has(`__collapsed:${parentId}`)
      : expanded.has(parentId);

  // Nó é visível se TODOS os ancestrais estão expandidos. O próprio nó
  // não precisa estar expandido — apenas seus pais.
  const visibleNodes = computed(() => {
    const byId = new Map(flatNodes.value.map(n => [n.id, n]));
    const expanded = expandedIds.value;
    return flatNodes.value.filter(node =>
      getAncestorIds(node.id, byId).every(id => ancestorOpen(id, expanded))
    );
  });

  const isExpanded = id => {
    if (defaultExpanded) return !expandedIds.value.has(`__collapsed:${id}`);
    return expandedIds.value.has(id);
  };

  const setExpanded = (id, value) => {
    // Set é reativo, mas reatribuir força propagação no useStorage.
    const next = new Set(expandedIds.value);
    if (defaultExpanded) {
      const key = `__collapsed:${id}`;
      if (value) next.delete(key);
      else next.add(key);
    } else if (value) {
      next.add(id);
    } else {
      next.delete(id);
    }
    expandedIds.value = next;
  };

  const expand = id => setExpanded(id, true);
  const collapse = id => setExpanded(id, false);
  const toggle = id => setExpanded(id, !isExpanded(id));

  // Útil pra navegação automática — expande todos os pais do node alvo
  // pra que ele apareça na sidebar.
  const expandPathTo = id => {
    const byId = new Map(flatNodes.value.map(n => [n.id, n]));
    if (!byId.has(id)) return;
    const ancestors = getAncestorIds(id, byId);
    if (ancestors.length === 0) return;
    const next = new Set(expandedIds.value);
    ancestors.forEach(ancestorId => {
      if (defaultExpanded) next.delete(`__collapsed:${ancestorId}`);
      else next.add(ancestorId);
    });
    expandedIds.value = next;
  };

  // Aviso em dev se IDs duplicados (quebra a unicidade do keyed render
  // e a expansão fica ambígua).
  if (import.meta.env?.DEV) {
    watch(
      flatNodes,
      nodes => {
        const seen = new Set();
        const dups = [];
        nodes.forEach(n => {
          if (seen.has(n.id)) dups.push(n.id);
          seen.add(n.id);
        });
        if (dups.length) {
          // eslint-disable-next-line no-console
          console.warn('[useSidebarTree] IDs duplicados detectados:', dups);
        }
      },
      { immediate: true }
    );
  }

  return {
    flatNodes,
    visibleNodes,
    expandedIds: computed(() => expandedIds.value),
    isExpanded,
    expand,
    collapse,
    toggle,
    expandPathTo,
  };
}
